import { Box, Tooltip, Typography } from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridColumnGroupingModel,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { common, economic, input, reduce } from "../../../i18n";
import dayjs, { Dayjs } from "dayjs";
import alertService from "../../../components/alert/alertService";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { EcoEfficiencyContext } from "../../../contexts/EcoEfficiencyContext";
import { EcoEfficiency } from "../../../models/dto/response/ResponseEcoEfficiency";
import { EcoEfficiencyService } from "../../../service/eco-efficiency.service";
import NumberFormatterUtils from "../../../utils/numberFormatterUtils";
import { tableStyle } from "../../emission/_components/data-table/EmissionDataTableStyle";
import { QuickSearchToolbar } from "./QuickSearchToolbar";
import { AddEcoEfficiencyLayout } from "./layout";
import { AddEcoEfficiency } from "./AddEcoEfficiency";
import EditIcon from "../../../components/icon/EditIcon";
import { isEcoFPlanSettingOpen } from "../../../config/config";
import { PlanSetting } from "./_components/plan-setting/PlanSetting";
import { ActualData } from "./_components/actual-data/ActualData";

const tooltipStyle = {
  tooltip: {
    style: {
      backgroundColor: "#dfdede",
      opacity: 1,
      color: "var(--dark)",
    },
  },
};

export const EcoEfficiencyDataGrid = () => {
  const { t, i18n } = useTranslation([common, input, economic, reduce], {
    nsMode: "fallback",
  });

  const {
    ecoEfficiency,
    setEcoEfficiency,
    setEditData,
    setModalIsOpen,
    filterSubOrganization,
    modalIsOpen,
    section,
    setSection,
    searchText,
    setSearchText,
  } = EcoEfficiencyContext();

  const deleteEcoF = async (row: EcoEfficiency) => {
    const isConfirm = await alertService.confirm(
      t("Delete"),
      <Trans
        t={t}
        i18nKey="Are-You-Sure-To-Delete-Eco-Efficiency"
        values={{ runningNo: row.id }}
        components={{
          span: <span style={{ color: "var(--red)" }} />,
        }}
      />,
    );

    if (isConfirm) {
      const refId: string = row._id;

      try {
        const result = await EcoEfficiencyService.deleteEcoEfficiency(refId);

        if (result) {
          setEcoEfficiency(result);
          alertService.mixin(t("Delete-Simulation-Success"));
        }
      } catch {
        // error alert already shown by service
      }
    }
  };

  const [rows, setRows] = useState<EcoEfficiency[]>([]);

  const columnGroup: GridColumnGroupingModel = [
    {
      groupId: t("Economic-Value"),
      children: [
        // { field: "plan" },
        { field: "actual" },
      ],
      headerAlign: "center",
    },
    {
      groupId: t("Normalization"),
      children: [
        { field: "economicValueNormalized" },
        { field: "environmentImpactNormalized" },
        { field: "ecoEfficiencyNormalized" },
      ],
      headerAlign: "center",
    },
  ];

  const columns: GridColDef<EcoEfficiency>[] = [
    {
      field: "id",
      headerName: t("ID"),
      width: 80,
      type: "string",
      headerAlign: "left",
      align: "left",
      valueFormatter: (value) => `EE-${value}`,
    },
    {
      field: "startDate",
      headerName: t("Period"),
      minWidth: 100,
      type: "date",
      headerAlign: "left",
      align: "left",
      sortable: true,
      valueFormatter: (value: any, row: any) => {
        const start: Dayjs = dayjs(value.substring(0, 10));

        const end: Dayjs = dayjs(row.endDate.substring(0, 10));

        if (start.isSame(end, "month")) {
          return start.locale(i18n.language).format("MMM YYYY");
        } else {
          return `${start.locale(i18n.language).format("MMM YYYY")} -\n${end
            .locale(i18n.language)
            .format("MMM YYYY")}`;
        }
      },
      renderCell: (params) => {
        return (
          <Typography
            variant="text1"
            sx={{
              whiteSpace: "pre-line",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "2",
              WebkitBoxOrient: "vertical",
            }}
          >
            {params.formattedValue}
          </Typography>
        );
      },
    },
    {
      field: "suborganizationRef",
      headerName: t("Organizations"),
      headerAlign: "left",
      minWidth: 140,
      sortable: true,
      type: "string",
      valueGetter: (value, row) =>
        row.suborganizationRef
          ? row.suborganizationRef.name
          : row.organizationRef.name,
      flex: 1,
    },
    {
      field: "economicindicatorsRef",
      headerName: t("Indicator-Name-Table"),
      headerAlign: "left",
      minWidth: 140,
      flex: 1,
      valueGetter: (value, row) =>
        i18n.language === "th"
          ? row.economicindicatorsRef.nameTh
          : row.economicindicatorsRef.nameEn,
    },
    // {
    //   field: "economicValuePlanNormalized",
    //   headerName: t("Plan"),
    //   headerAlign: "right",
    //   type: "number",
    //   minWidth: 130,
    //   valueFormatter: (value: number) => value.toFixed(2) ?? "-",
    //   renderCell: (params) => {
    //     return (
    //       <Box
    //         display={"flex"}
    //         flexDirection={"row"}
    //         gap={"8px"}
    //         alignItems={"center"}
    //         justifySelf={"end"}
    //       >
    //         {params.formattedValue}

    //         <EditIcon
    //           style={{
    //             borderRadius: "4px",
    //             padding: "4px",
    //             backgroundColor: isEcoFPlanSettingOpen
    //               ? "var(--green)"
    //               : "var(--gray2)",
    //           }}
    //         />
    //       </Box>
    //     );
    //   },
    // },
    {
      field: "economicValueNormalized",
      headerName: t("Economic-Value"),
      headerAlign: "right",
      minWidth: 130,
      type: "number",
      valueFormatter: (value: number) => value.toFixed(2) ?? "-",
      renderCell: (params) => {
        return (
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={"8px"}
            alignItems={"center"}
            justifySelf={"end"}
          >
            {params.formattedValue}

            <EditIcon
              style={{
                borderRadius: "4px",
                padding: "4px",
                backgroundColor: "var(--green)",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "environmentImpactNormalized",
      headerName: t("Environmental-Impact-Value"),
      headerAlign: "right",
      minWidth: 220,
      type: "number",
      flex: 1,
      valueFormatter: (value) => `${NumberFormatterUtils.numberFormat(value)}`,
      renderCell: (params) => {
        return (
          <Tooltip
            title={`${params.row.environmentalImpactType}`}
            slotProps={{
              ...tooltipStyle,
            }}
          >
            <span>{NumberFormatterUtils.numberFormat(params.value)}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "ecoEfficiencyNormalized",
      headerName: t("Eco-Efficiency"),
      headerAlign: "right",
      minWidth: 180,
      type: "number",
      valueFormatter: (value) => `${NumberFormatterUtils.numberFormat(value)}`,
      flex: 1,
    },
    {
      field: "baseYear",
      headerName: t("Base-Year"),
      headerAlign: "center",
      minWidth: 80,
      type: "string",
      align: "center",
    },
    {
      field: "",
      headerName: "",
      sortable: false,
      type: "actions",
      width: 40,
      getApplyQuickFilterFn: undefined,
      getActions: (params) => {
        return [
          <GridActionsCellItem
            key={`${params.row._id}-more`}
            icon={<img src="/img/delete.svg" alt="icon-more" />}
            label="more"
            id="basic-button"
            aria-haspopup="true"
          />,
        ];
      },
    },
  ];

  useEffect(() => {
    if (filterSubOrganization) {
      setRows([
        ...ecoEfficiency.filter((e: EcoEfficiency) => {
          return filterSubOrganization.includes(
            e.suborganizationRef
              ? e.suborganizationRef._id
              : e.organizationRef._id,
          );
        }),
      ]);
    } else {
      setRows([...ecoEfficiency]);
    }
  }, [ecoEfficiency, filterSubOrganization]);

  return (
    <Box sx={{ height: "100%", width: "100%", minHeight: 480 }}>
      {modalIsOpen && section === "Details" && (
        <AddEcoEfficiencyLayout>
          <AddEcoEfficiency />
        </AddEcoEfficiencyLayout>
      )}

      {modalIsOpen && section === "Plan-Setting" && (
        <AddEcoEfficiencyLayout>
          <PlanSetting />
        </AddEcoEfficiencyLayout>
      )}

      {modalIsOpen && section === "Add-Actual-Data" && (
        <AddEcoEfficiencyLayout>
          <ActualData />
        </AddEcoEfficiencyLayout>
      )}

      {modalIsOpen === false && (
        <Box height={"100%"}>
          <AppDataGrid
            rows={rows}
            columnGroupingModel={columnGroup}
            columns={
              isEcoFPlanSettingOpen
                ? columns
                : columns.filter(
                    (c) => c.field !== "economicValuePlanNormalized",
                  )
            }
            rowHeight={60}
            slots={{ toolbar: QuickSearchToolbar }}
            localeText={{
              toolbarQuickFilterPlaceholder: `${t("search")}...`,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
              },
            }}
            filterModel={{
              items: [],
              quickFilterValues: searchText
                ? searchText.split(" ").filter((word) => word !== "")
                : [],
            }}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 50 },
              },
            }}
            pageSizeOptions={[25, 50, 100]}
            sx={{
              ...tableStyle(),
              height: "100%",
            }}
            getRowId={(row) => row._id}
            disableMultipleRowSelection
            onCellClick={(param, event, details) => {
              const action = () => {
                setModalIsOpen(true);
                setEditData(param.row);
              };

              if (param.field === "") {
                deleteEcoF(param.row);
              } else if (param.field === "economicValuePlanNormalized") {
                setSection("Plan-Setting");
                action();
              } else if (param.field === "economicValueNormalized") {
                setSection("Add-Actual-Data");
                action();
              } else {
                setSection("Details");
                action();
              }
            }}
            autosizeOnMount
          />
        </Box>
      )}
    </Box>
  );
};
